import { Avatar, Style } from '@dicebear/core';
import personas from '@dicebear/styles/personas.json';
import { useMemo } from 'react';

import type { Persona } from '@repo/contracts';

import { accentOf, initialsOf } from './persona-accent';
import { toDiceBearOptions } from './persona-avatar-options';

/** One for the whole app: constructing it parses the style definition, and it
 *  holds no per-avatar state. */
export const personasStyle = new Style(personas);

/** Size, radius and monogram type together, since each is tuned to the
 *  others. `row` is a list row, `xl` a profile header, `xs` a reaction. */
const SIZES = {
  lg: 'size-11 rounded-full text-ui',
  md: 'size-9 rounded-full text-xs',
  row: 'size-8 rounded-full text-tag',
  sm: 'size-7 rounded-full text-badge-lg',
  xl: 'size-12 rounded-full text-ui-lg',
  // A participant's portrait in the research report.
  hero: 'size-20 rounded-full text-card-title',
  // A face in one of the start screens' speech bubbles.
  bubble: 'size-[40px] rounded-full text-xs',
  // The overlapping faces on home's empty state.
  stack: 'size-14 rounded-full text-ui-lg',
  // A face in a reactions row — who would nod along, at a glance.
  xs: 'size-4 rounded-full text-micro',
} as const;

/**
 * A panel member's face: the portrait the model chose for them, in a white
 * circle, or a coloured monogram for anybody written before avatars existed
 * (the tint is what makes a monogram readable, so it keeps one).
 *
 * `aria-hidden` wherever the name is rendered beside it — the face is a
 * second copy of a label already on screen, and a screen reader announcing
 * "MI Maya Iqbal" is noise. Pass `label` only when the avatar stands alone (a
 * stack of faces on a feedback row, say).
 */
export function PersonaAvatar({
  className = '',
  label,
  persona,
  size = 'md',
}: {
  /** Extra utilities for state, like dimming somebody off the panel. */
  className?: string;
  label?: string;
  persona: Pick<Persona, 'accent' | 'avatarProfile' | 'id' | 'name'>;
  size?: keyof typeof SIZES;
}) {
  const accent = accentOf(persona);
  const { avatarProfile, id } = persona;

  // A malformed colour (a hand-edited row, a style upgrade changing a ramp)
  // throws inside DiceBear. The monogram always renders, so it stays the floor
  // rather than one bad face taking down the panel it sits in. An unknown
  // variant does not throw — DiceBear quietly picks one from the seed — which
  // is why persona-avatar.test.tsx checks the allowed values instead.
  const portrait = useMemo(() => {
    if (!avatarProfile) return null;
    try {
      return new Avatar(
        personasStyle,
        toDiceBearOptions(avatarProfile, id),
      ).toDataUri();
    } catch {
      return null;
    }
  }, [avatarProfile, id]);

  const a11y = label
    ? { 'aria-label': label, role: 'img' }
    : { 'aria-hidden': 'true' as const };

  if (portrait) {
    return (
      <img
        alt={label ?? ''}
        aria-hidden={label ? undefined : 'true'}
        className={`shrink-0 bg-surface object-cover ${SIZES[size]} ${className}`}
        src={portrait}
        title={label}
      />
    );
  }

  return (
    <span
      {...a11y}
      className={`flex shrink-0 items-center justify-center font-semibold ${accent.fill} ${accent.ink} ${SIZES[size]} ${className}`}
      title={label}
    >
      {initialsOf(persona.name)}
    </span>
  );
}
